// Pure helpers for the custom POI category layer. No React, no app imports.
// Side-effecting map helpers (addLayer/setFilter) live in Task 3 of the same file.

export const BASE_POI_LAYER = 'poi-label';
export const CUSTOM_POI_LAYER = 'vansite-poi';
export const POI_SOURCE = 'composite';
export const POI_SOURCE_LAYER = 'poi_label';
export const DEFAULT_POI_ICON = 'vansite-poi-default';

// class/maki lists are a known-unknown: validate against real DACH tiles in Task 10.
export const POI_CATEGORIES = [
  { id: 'sights', iconId: 'vansite-poi-sights',
    class: ['landmark', 'historic', 'arts_and_entertainment'],
    maki: ['castle', 'monument', 'museum', 'art-gallery', 'viewpoint'] },
  { id: 'nature', iconId: 'vansite-poi-nature',
    class: ['park_like'],
    maki: ['park', 'garden', 'picnic-site', 'dog-park', 'playground'] },
  // Supply deliberately does NOT use class:'motorist' — that class is mostly
  // parking + parking-garage + charging-station, which floods the map. Match
  // only the resupply makis. Fuel is its own category below.
  { id: 'supply', iconId: 'vansite-poi-supply',
    class: [],
    maki: ['drinking-water', 'toilet', 'grocery', 'laundry'] },
  // Fuel stations (mapbox-streets-v8 tags them maki:'fuel', class:'motorist').
  // Match on the maki alone so parking/charging (also class motorist) stay out.
  // NOTE: the tileset only carries fuel POIs from ~zoom 15 up, and sparsely —
  // they won't appear at region/trip zoom. That's a hard data limit.
  { id: 'fuel', iconId: 'vansite-poi-fuel',
    class: [],
    maki: ['fuel'] },
  { id: 'cityFood', iconId: 'vansite-poi-cityfood',
    class: ['food_and_drink', 'commercial_services', 'public_facilities', 'medical'],
    maki: [] },
];

export const POI_CATEGORY_IDS = POI_CATEGORIES.map(c => c.id);

const uniq = (arr) => Array.from(new Set(arr));

export const buildCategoryFilter = (activeIds) => {
  const active = POI_CATEGORIES.filter(c => activeIds.includes(c.id));
  if (active.length === 0) return false;
  const classes = uniq(active.flatMap(c => c.class));
  const makis = uniq(active.flatMap(c => c.maki));
  const classExpr = classes.length ? ['match', ['get', 'class'], classes, true, false] : false;
  const makiExpr = makis.length ? ['match', ['get', 'maki'], makis, true, false] : false;
  return ['any', classExpr, makiExpr];
};

export const buildIconImageExpression = () => {
  const makiPairs = POI_CATEGORIES.flatMap(c => c.maki.flatMap(m => [m, c.iconId]));
  const classPairs = POI_CATEGORIES.flatMap(c => c.class.flatMap(cl => [cl, c.iconId]));
  const classFallback = classPairs.length
    ? ['match', ['get', 'class'], ...classPairs, DEFAULT_POI_ICON]
    : DEFAULT_POI_ICON;
  return makiPairs.length
    ? ['match', ['get', 'maki'], ...makiPairs, classFallback]
    : classFallback;
};

const POI_TEXT_COLOR = '#0E3B43'; // petrol.main

export const ensureCustomPoiLayer = (map) => {
  if (!map || map.getLayer(CUSTOM_POI_LAYER)) return;
  map.addLayer({
    id: CUSTOM_POI_LAYER,
    type: 'symbol',
    source: POI_SOURCE,
    'source-layer': POI_SOURCE_LAYER,
    // No minzoom: show POIs at every zoom the tiles provide. (The mapbox-streets
    // poi_label source itself has no features below ~z6, so nothing renders there.)
    layout: {
      'icon-image': buildIconImageExpression(),
      'icon-size': 1.4, // larger, easy-to-spot pins
      'icon-anchor': 'bottom', // teardrop tip sits on the POI location
      'icon-allow-overlap': true, // a toggled-on category must always be visible
      'text-field': ['get', 'name'],
      'text-size': 11,
      'text-offset': [0, 0.4], // just below the pin tip
      'text-anchor': 'top',
      'text-optional': true, // labels declutter; icons never do
    },
    paint: { 'text-color': POI_TEXT_COLOR, 'text-halo-color': '#FFFFFF', 'text-halo-width': 1.2 },
    filter: false, // start hidden; applyPoiCategoryFilter drives visibility
  });
  if (map.getLayer(BASE_POI_LAYER)) {
    map.setLayoutProperty(BASE_POI_LAYER, 'visibility', 'none');
  }
};

// NOTE: we deliberately do NOT combine with poi-label's own filter. That filter
// gates on `filterrank` (a per-POI importance rank) and only admits rank<=0 below
// zoom 16 — which hides virtually all of our curated category POIs at normal
// browsing zooms. The category filter alone drives visibility; Mapbox's symbol
// collision handles label decluttering.
export const applyPoiCategoryFilter = (map, activeIds) => {
  if (!map || !map.getLayer(CUSTOM_POI_LAYER)) return;
  map.setFilter(CUSTOM_POI_LAYER, buildCategoryFilter(activeIds));
};
